import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Users, Package, DollarSign, TrendingUp, CheckCircle, Clock, AlertCircle, Search, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOverview = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalPayments: 0,
    pendingUTR: 0,
    deliveredOrders: 0,
    rtoOrders: 0
  });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOverview, setUserOverview] = useState(null);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [usersRes, ordersRes, paymentsRes, utrRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/utr-submissions`)
      ]);

      setUsers(usersRes.data);
      
      const orders = ordersRes.data || [];
      const payments = paymentsRes.data || [];
      const utrSubmissions = utrRes.data || [];

      setStats({
        totalUsers: usersRes.data?.length || 0,
        totalOrders: orders.length,
        totalPayments: payments.reduce((sum, p) => sum + (parseFloat(p.planPrice) || parseFloat(p.amount) || 0), 0),
        pendingUTR: utrSubmissions.filter(u => u.status === 'Pending').length,
        deliveredOrders: orders.filter(o => o.status === 'Delivered').length,
        rtoOrders: orders.filter(o => o.status === 'RTO').length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setOverviewOpen(true);
    try {
      const response = await axios.get(`${API}/admin/user-overview/${user.principalId}`);
      setUserOverview(response.data);
    } catch (error) {
      console.error('Error fetching user overview:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.phone?.includes(search) ||
      user.clientId?.toString().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-in fade-in">
        Admin Dashboard Overview
      </h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-blue-500/30 hover:border-blue-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Users</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-400 mt-1">{stats.totalUsers}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <Users className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/10 border-slate-200 hover:border-purple-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2 delay-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Orders</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-600 mt-1">{stats.totalOrders}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <Package className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-200 hover:border-green-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2 delay-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 mt-1">₹{stats.totalPayments.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <DollarSign className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/10 to-blue-500/10 border-yellow-500/30 hover:border-yellow-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2 delay-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Pending UTR</p>
                <p className="text-2xl md:text-3xl font-bold text-yellow-400 mt-1">{stats.pendingUTR}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-blue-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <Clock className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/30 hover:border-emerald-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2 delay-400">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">Delivered</p>
                <p className="text-2xl md:text-3xl font-bold text-emerald-400 mt-1">{stats.deliveredOrders}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <CheckCircle className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-500/30 hover:border-red-500/50 transition-all hover:scale-105 group animate-in fade-in slide-in-from-bottom-2 delay-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 font-medium">RTO Orders</p>
                <p className="text-2xl md:text-3xl font-bold text-red-400 mt-1">{stats.rtoOrders}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg group-hover:scale-110 transition-all shadow-lg">
                <AlertCircle className="w-5 h-5 text-slate-800" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Search & Summary Section */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="text-xl bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Quick User Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search users by Name, Email, Phone, Client ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
            />
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No users found</p>
            ) : (
              filteredUsers.slice(0, 10).map(user => (
                <div 
                  key={user.principalId} 
                  className="flex items-center justify-between p-4 bg-white rounded-lg hover:bg-slate-100 transition-all group cursor-pointer"
                  onClick={() => handleViewUser(user)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-500 rounded-full flex items-center justify-center text-slate-800 font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-slate-800 font-medium">{user.name}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-blue-400 font-bold">#{user.clientId}</p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        user.activePlan ? 'bg-green-100 text-green-300' : 'bg-slate-500/20 text-slate-500'
                      }`}>
                        {user.activePlan || 'No Plan'}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Overview Dialog */}
      <Dialog open={overviewOpen} onOpenChange={setOverviewOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-600">
              User Summary - #{selectedUser?.clientId}
            </DialogTitle>
          </DialogHeader>
          
          {userOverview ? (
            <div className="space-y-4">
              {/* User Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-white rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Name</p>
                  <p className="text-slate-800 font-medium">{userOverview.user?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Email</p>
                  <p className="text-slate-800">{userOverview.user?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Phone</p>
                  <p className="text-slate-800">{userOverview.user?.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Plan</p>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    userOverview.user?.activePlan ? 'bg-green-100 text-green-300' : 'bg-slate-500/20 text-slate-500'
                  }`}>
                    {userOverview.user?.activePlan || 'No Plan'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Total Orders</p>
                  <p className="text-blue-400 font-bold">{userOverview.stats?.totalOrders || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-1">Total Payments</p>
                  <p className="text-green-600 font-bold">₹{userOverview.stats?.totalPayments?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Onboarding Status */}
              {userOverview.onboarding && (
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-sm text-slate-600 font-medium mb-3">Onboarding Progress</p>
                  <div className="flex flex-wrap gap-2">
                    {['purchaseStatus', 'whatsappStatus', 'websiteStatus', 'campaignStatus', 'adsStatus'].map((step) => (
                      <span key={step} className={`px-2 py-1 rounded text-xs font-medium ${
                        userOverview.onboarding[step] === 'Completed' ? 'bg-green-100 text-green-300' :
                        userOverview.onboarding[step] === 'Live' ? 'bg-blue-500/20 text-blue-300' :
                        userOverview.onboarding[step] === 'Pending' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-slate-500/20 text-slate-500'
                      }`}>
                        {step.replace('Status', '')}: {userOverview.onboarding[step] || 'Not Started'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500">Loading...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOverview;
